import { useEffect, useRef, useState, useCallback, useContext } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext, API } from "../App";

/**
 * Synchronized YouTube watch-party player.
 *
 * - Uses the YouTube IFrame API so we can read currentTime and call seekTo / playVideo
 *   / pauseVideo programmatically.
 * - Anyone in the room can play / pause / seek. Each user action is POSTed to the
 *   backend, which broadcasts a `movie_state_update` over the room WebSocket. The
 *   ChatRoomPage forwards those WS events as `window` events: `movie:state` and
 *   `movie:reaction`.
 * - Drift correction: every 1.5s we compare local currentTime to the server's expected
 *   playhead. If |drift| > 2s we hard-seek to the expected position.
 *
 * Props:
 *   roomId      - movie room id (movie-theater / movie-theater-1..3 / movies-tv)
 *   videoId     - YouTube video id currently playing in this room
 *   initialTime - seconds to jump to on first ready (for late-joiners)
 *   initialPlaying - whether the room is currently in `playing` state
 *   onEnded     - called when the video naturally finishes
 *
 * @noinspection JSUnusedGlobalSymbols
 */
const SyncedYouTubePlayer = ({
  roomId,
  videoId,
  initialTime = 0,
  initialPlaying = true,
  onEnded,
}) => {
  const { token, user } = useContext(AuthContext);
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [reactions, setReactions] = useState([]); // floating emoji animations

  // Refs to suppress our own play/pause echo from the WS broadcast.
  // After we call the API for an action we ignore inbound state updates for 1.5s.
  const lastLocalActionAtRef = useRef(0);
  const ignoreNextStateRef = useRef(false);

  // ----- Helpers ---------------------------------------------------------------
  const safeCall = (fn) => {
    try { return fn(); } catch (_e) { return null; }
  };

  const getCurrentTime = () =>
    safeCall(() => playerRef.current?.getCurrentTime?.()) ?? 0;

  const post = useCallback(async (path, body = null) => {
    if (!token) return null;
    try {
      const url = `${API}/movie-sync/${roomId}${path}`;
      const res = await axios.post(url, body || {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    } catch (err) {
      console.error(`[movie-sync] ${path} failed`, err);
      return null;
    }
  }, [token, roomId]);

  // ----- Load YouTube IFrame API once ------------------------------------------
  useEffect(() => {
    if (window.YT && window.YT.Player) return;
    if (document.getElementById("youtube-iframe-api")) return;
    const tag = document.createElement("script");
    tag.id = "youtube-iframe-api";
    tag.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(tag);
  }, []);

  // ----- Create / re-create the player when videoId changes --------------------
  useEffect(() => {
    if (!videoId || !containerRef.current) return;
    let cancelled = false;
    setPlayerReady(false);

    const init = () => {
      if (cancelled || !containerRef.current) return;
      // Destroy any previous instance.
      if (playerRef.current?.destroy) {
        safeCall(() => playerRef.current.destroy());
        playerRef.current = null;
      }
      // Create a host node inside our container (YT replaces the node).
      containerRef.current.innerHTML = "";
      const host = document.createElement("div");
      host.id = `yt-host-${roomId}`;
      containerRef.current.appendChild(host);

      playerRef.current = new window.YT.Player(host.id, {
        videoId,
        playerVars: {
          autoplay: initialPlaying ? 1 : 0,
          controls: 1,
          modestbranding: 1,
          rel: 0,
          start: Math.max(0, Math.floor(initialTime || 0)),
          playsinline: 1,
        },
        events: {
          onReady: (e) => {
            if (cancelled) return;
            setPlayerReady(true);
            // Snap to the late-join position.
            if (initialTime > 1) {
              safeCall(() => e.target.seekTo(initialTime, true));
            }
            if (initialPlaying) {
              safeCall(() => e.target.playVideo());
            } else {
              safeCall(() => e.target.pauseVideo());
            }
          },
          onStateChange: (e) => {
            // YT.PlayerState: -1 unstarted, 0 ended, 1 playing, 2 paused, 3 buffering, 5 cued
            if (e.data === window.YT.PlayerState.ENDED) {
              onEnded?.();
              return;
            }
            // Ignore state events that we caused programmatically (during reconciliation).
            if (ignoreNextStateRef.current) {
              ignoreNextStateRef.current = false;
              return;
            }
            const now = Date.now();
            // Detect user-initiated play/pause and propagate.
            if (e.data === window.YT.PlayerState.PLAYING) {
              lastLocalActionAtRef.current = now;
              post("/play", {
                video_id: videoId,
                video_title: "",
                time: getCurrentTime(),
              });
            } else if (e.data === window.YT.PlayerState.PAUSED) {
              lastLocalActionAtRef.current = now;
              post("/pause", { time: getCurrentTime() });
            }
          },
        },
      });
    };

    if (window.YT && window.YT.Player) {
      init();
    } else {
      // The API will call this global when ready. Chain so we don't clobber existing.
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof prev === "function") prev();
        init();
      };
    }

    return () => {
      cancelled = true;
      if (playerRef.current?.destroy) {
        safeCall(() => playerRef.current.destroy());
        playerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, roomId]);

  // ----- Detect user seeks ----------------------------------------------------
  // YouTube doesn't fire a seek event, so we poll: if the playhead jumps by
  // more than ~1.5s in 250ms, treat it as a user seek and tell the backend.
  useEffect(() => {
    if (!playerReady) return;
    let prev = getCurrentTime();
    let prevAt = Date.now();
    const id = setInterval(() => {
      if (!playerRef.current) return;
      const t = getCurrentTime();
      const dt = (Date.now() - prevAt) / 1000;
      const expectedDelta = dt; // 1.0x speed = 1 sec/sec
      if (dt > 0 && Math.abs(t - prev - expectedDelta) > 1.5) {
        // It's a user-initiated jump.
        if (Date.now() - lastLocalActionAtRef.current > 600) {
          lastLocalActionAtRef.current = Date.now();
          post("/seek", { time: t });
        }
      }
      prev = t;
      prevAt = Date.now();
    }, 250);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerReady]);

  // ----- WS listener: reconcile against server state --------------------------
  useEffect(() => {
    const onState = (ev) => {
      const data = ev.detail;
      if (!data || data.room_id !== roomId) return;
      if (!playerRef.current || !playerReady) return;
      // If we just sent this same action, ignore the echo.
      if (data.actor && data.actor === user?.username &&
          Date.now() - lastLocalActionAtRef.current < 1500) {
        return;
      }
      if (data.action === "end" || data.active === false) {
        // host of the panel will handle UI; just stop our player.
        safeCall(() => playerRef.current.pauseVideo());
        return;
      }
      // Tight sync: hard-seek if drift > 2s.
      const localTime = getCurrentTime();
      const serverTime = Number(data.current_time || 0);
      if (Math.abs(localTime - serverTime) > 2) {
        ignoreNextStateRef.current = true;
        safeCall(() => playerRef.current.seekTo(serverTime, true));
      }
      // Mirror play/pause.
      const playerState = safeCall(() => playerRef.current.getPlayerState?.());
      if (data.is_playing && playerState !== 1) {
        ignoreNextStateRef.current = true;
        safeCall(() => playerRef.current.playVideo());
      } else if (!data.is_playing && playerState === 1) {
        ignoreNextStateRef.current = true;
        safeCall(() => playerRef.current.pauseVideo());
      }
    };
    const onReaction = (ev) => {
      const data = ev.detail;
      if (!data || data.room_id !== roomId) return;
      const id = `${Date.now()}-${Math.random()}`;
      // Random horizontal start between 10% and 90%
      const left = 10 + Math.random() * 80;
      setReactions(prev => [...prev, { id, emoji: data.emoji, left, username: data.username }]);
      // Auto-remove after animation
      setTimeout(() => {
        setReactions(prev => prev.filter(r => r.id !== id));
      }, 2200);
    };
    window.addEventListener("movie:state", onState);
    window.addEventListener("movie:reaction", onReaction);
    return () => {
      window.removeEventListener("movie:state", onState);
      window.removeEventListener("movie:reaction", onReaction);
    };
  }, [roomId, playerReady, user?.username]);

  // ----- Reactions: emoji bar ------------------------------------------------
  const REACTION_EMOJIS = ["👏", "❤️", "😂", "🔥", "😮", "😢", "🤣"];
  const sendReaction = async (emoji) => {
    // Optimistically render our own reaction
    const id = `${Date.now()}-self-${Math.random()}`;
    const left = 10 + Math.random() * 80;
    setReactions(prev => [...prev, { id, emoji, left, username: user?.username }]);
    setTimeout(() => setReactions(prev => prev.filter(r => r.id !== id)), 2200);
    try {
      await axios.post(`${API}/movie-sync/${roomId}/react`, { emoji }, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      console.error("Failed to send reaction", err);
      toast.error("Couldn't send reaction");
    }
  };

  return (
    <div className="relative w-full h-full bg-black" data-testid="synced-youtube-player">
      {/* Player container — YT.Player will render an iframe inside this div */}
      <div ref={containerRef} className="absolute inset-0" />

      {/* Floating reactions overlay */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {reactions.map(r => (
          <div
            key={r.id}
            className="absolute bottom-16 text-3xl select-none animate-float-up"
            style={{ left: `${r.left}%` }}
            title={r.username}
          >
            {r.emoji}
          </div>
        ))}
      </div>

      {/* Reaction emoji bar — bottom of the player */}
      <div className="absolute left-1/2 -translate-x-1/2 bottom-1 z-20 flex gap-1 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1 pointer-events-auto" data-testid="movie-reactions-bar">
        {REACTION_EMOJIS.map(em => (
          <button
            key={em}
            onClick={() => sendReaction(em)}
            className="text-xl hover:scale-125 transition-transform px-1"
            title={`React ${em}`}
            data-testid={`movie-react-${em}`}
          >
            {em}
          </button>
        ))}
      </div>
    </div>
  );
};

export default SyncedYouTubePlayer;
